import {
  Box,
  Collapse,
  Typography,
  Switch,
  FormControlLabel,
  Modal,
  IconButton,
  Button,
} from '@mui/material';
import { TextAlignCenter, Gear } from '@phosphor-icons/react';
import { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useEdges, useReactFlow } from 'reactflow';
import { MailService } from '../../service/MailService';
import { WebhookService } from '../../service/WebhookService';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { useStoreGoogleData } from '../../store/storeGoogleData';
import { useAuthStore } from '../../store/storeAuth';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import ConnectToGoogleSheets from '../GoogleSheetsIntegration/ConnectToGoogleSheets';
import {
  getConnectedEmail,
  isGoogleSheetsAccessTokenValid,
  hasGmailScope,
} from '../GoogleSheetsIntegration/helpers';
import TagInput from '../Test/TagInput';
import Label from '../UI/Label/Label';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { useStoreMicrosoftData } from '../../store/storeMicrosoftData';
import { MicrosoftService } from '../../service/MicrosoftService';
import ConnectToMicrosoft from '../MicrosoftIntegration/ConnectToMicrosoft';

function OutputObjectNode({ data, type, id }: NodeProps) {
  const [formData, setFormData] = useState({
    enabledEmail: false,
    useFromPersonalGmail: false,
    useFromMicrosoft: false,
    useTemplate: true,
    enabledCallbackUrl: false,
    callbackUrl: '',
    emails: '',
    subject: '',
    text: '',
    urls: '',
  });
  const { setNodes: setNodeState } = useReactFlow();
  const setNotification = useNotificationStore(state => state.setNotification);
  const params = useParams();

  const { setSchema, setSaving, flowId } = useFlowsStore(state => state);
  const edges = useEdges();

  const { isSheetConnected, googleAccessToken } = useStoreGoogleData();
  const { user } = useAuthStore(state => state);

  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState<{
    value: string | boolean;
    name: string;
  }>({
    value: '',
    name: '',
  });
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');
  const [hasGmailScopeEnabled, setHasGmailScopeEnabled] = useState(false);

  const { isMicrosoftConnected } = useStoreMicrosoftData();
  const [connectedMicrosoftEmail, setConnectedMicrosoftEmail] = useState('');

  useDebounceEffect(
    () => {
      if (!isChangingDirectly) {
        setFormData({
          enabledEmail: data?.enabledEmail || false,
          useFromPersonalGmail: data?.useFromPersonalGmail || false,
          useFromMicrosoft: data?.useFromMicrosoft || false,
          useTemplate:
            data?.useTemplate !== undefined ? data.useTemplate : true,
          enabledCallbackUrl: data?.enabledCallbackUrl || false,
          callbackUrl:
            data.callbackUrlInputData?.[0]?.text || data.callbackUrl || '',
          emails: data.emails || '',
          subject: data.subject || '',
          text: data.text || '',
          urls: Array.isArray(data.urls) ? data.urls : [],
        });
      }
    },
    [data, isChangingDirectly],
    300,
  );

  const onChange = (e: { target: { name: string; value: any } }) => {
    const { name, value } = e.target;

    if (
      name === 'emails' ||
      name === 'subject' ||
      name === 'urls' ||
      name === 'callbackUrl'
    ) {
      let processedValue = value;

      if (name === 'emails') {
        const regex = /(\s|,)+/g;
        processedValue = value.replace(regex, ',');
      }

      if (name === 'urls') {
        processedValue = value.split(',').map((url: string) => url.trim()); // Splitting input into an array of URLs
      }

      setFormData({
        ...formData,
        [name]: processedValue,
      });

      setIsChangingDirectly(true);
      setWhoIsChanging({
        value: processedValue,
        name,
      });
    }
  };

  const handleToggleChange = (name: string, value: boolean) => {
    setFormData({
      ...formData,
      [name]: value,
    });

    setIsChangingDirectly(true);
    setWhoIsChanging({
      value,
      name,
    });
  };

  const sendEmails = useCallback(
    async (mailData: {
      emails: string;
      subject: string;
      text: string;
      urlsData: { text: string }[];
      callbackUrl?: string;
    }) => {
      try {
        const types = ['emails', 'subject', 'text', 'urls', 'callbackUrl'];

        let newMailData: any = { ...mailData };

        types.forEach(type => {
          if (data?.[`${type}Data`] && data?.[`${type}Data`].length > 0) {
            newMailData[type] = data[`${type}Data`]
              .map((item: { text: string }) => item.text)
              .join(',');
          }
        });

        if (newMailData.callbackUrl && newMailData.callbackUrl.trim()) {
          try {
            new URL(newMailData.callbackUrl.trim());
            newMailData.callbackUrl = newMailData.callbackUrl.trim();
          } catch (error) {
            console.warn('Invalid callback URL:', newMailData.callbackUrl);
            delete newMailData.callbackUrl;
          }
        } else {
          delete newMailData.callbackUrl;
        }

        let emails = newMailData?.emails?.trim()?.split(',');
        if (!params.flowId) {
          return setNotification({
            type: 'error',
            message: 'No agent id found',
          });
        }

        if (!emails || emails.length === 0) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            setSaving,
            objectData: {
              canSendMail: false,
            },
            setSchema,
          });
          return;
        }

        const result =
          data?.urlsData && data?.urlsData?.length > 0
            ? data?.urlsData
                ?.filter((url: { text: string }) => url && url.text)
                .map((url: { text: string }) => url.text)
            : data && data.urls && data.urls.length > 0
            ? data.urls
            : [];

        if (emails.length === 1 && emails[0] === '') {
          emails = [];
        }

        const useFromPersonalGmail =
          formData.useFromPersonalGmail ?? data?.useFromPersonalGmail ?? false;
        const useFromMicrosoft =
          formData.useFromMicrosoft ?? data?.useFromMicrosoft ?? false;

        if (useFromPersonalGmail && isSheetConnected) {
          let fromEmail = user?.email;
          if (googleAccessToken) {
            try {
              fromEmail = await getConnectedEmail(googleAccessToken);
            } catch (error) {
              console.error('Failed to get Google email:', error);
            }
          }
          await MailService.sendGmail({
            to: emails,
            subject: newMailData.subject,
            text: newMailData.text,
            attachments: result,
            callBackUrl: newMailData.callbackUrl,
            userEmail: fromEmail,
            useTemplate: formData.useTemplate,
          });

          setNotification({
            type: 'success',
            message: 'Email successfully sent via Gmail',
          });
        } else if (useFromMicrosoft && isMicrosoftConnected) {
          await MailService.sendMicrosoft({
            to: emails,
            subject: newMailData.subject,
            text: newMailData.text,
            attachments: result,
            callBackUrl: newMailData.callbackUrl,
            useTemplate: formData.useTemplate,
          });

          setNotification({
            type: 'success',
            message: 'Email successfully sent via Microsoft',
          });
        } else {
          if (useFromPersonalGmail && !isSheetConnected) {
            setNotification({
              type: 'warning',
              message: 'Not linked to Gmail, sending from system instead',
            });
          }

          if (useFromMicrosoft && !isMicrosoftConnected) {
            setNotification({
              type: 'warning',
              message: 'Not linked to Microsoft, sending from system instead',
            });
          }

          await MailService.send({
            emails,
            subject: newMailData.subject,
            text: newMailData.text,
            flowId: params.flowId,
            urls: result,
            callbackUrl: newMailData.callbackUrl,
            useTemplate: formData.useTemplate,
          });

          setNotification({
            type: 'success',
            message: 'Email successfully sent',
          });
        }

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          setSaving,
          id,
          objectData: {
            canSendMail: false,
          },
          setSchema,
        });
      } catch (error: any) {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          setSaving,
          id,
          objectData: {
            canSendMail: false,
          },
          setSchema,
        });

        setNotification({
          type: 'error',
          message: error?.message || 'Error sending emails',
        });
      }
    },
    [
      data,
      params.flowId,
      setNotification,
      edges,
      setNodeState,
      setSaving,
      id,
      setSchema,
      formData.useFromPersonalGmail,
      formData.useTemplate,
      isSheetConnected,
      user?.email,
      googleAccessToken,
    ],
  );

  const onExecuteNode = useMemo(() => {
    const values = {
      ...data,
    };
    return values;
  }, [data]);

  const debounceTimeoutRef = useRef<any>(null);

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          setSaving,
          id,

          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  useEffect(() => {
    if (data?.canSendMail) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        const myValues = onExecuteNode;

        if (myValues) {
          sendEmails(myValues);
        }
      }, 300);
    } else {
      clearTimeout(debounceTimeoutRef.current);
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [onExecuteNode, data?.canSendMail, sendEmails]);

  useEffect(() => {
    const triggerWebhookCallback = async () => {
      const callbackUrl =
        data.callbackUrlInputData?.[0]?.text ||
        data.callbackUrl ||
        formData.callbackUrl;

      if (!callbackUrl || !callbackUrl.trim()) {
        return;
      }

      if (!data?.canSendMail) {
        return;
      }

      try {
        const outputText =
          data?.textData
            ?.map((item: { text: string }) => item.text)
            ?.join(' ') || '';

        await WebhookService.triggerCallback(callbackUrl, { text: outputText });

        setNotification({
          type: 'success',
          message: 'Webhook triggered successfully',
        });
      } catch (error: any) {
        console.error('Webhook callback failed:', error);
        setNotification({
          type: 'error',
          message: `Webhook callback failed: ${
            error?.message || 'Unknown error'
          }`,
        });
      }
    };

    if (
      data?.canSendMail &&
      !formData.enabledEmail &&
      (data.callbackUrl ||
        formData.callbackUrl ||
        data.callbackUrlInputData?.[0]?.text)
    ) {
      triggerWebhookCallback();
    }
  }, [data, formData, setNotification]);

  useEffect(() => {
    const getConnectedEmailHandler = async () => {
      if (isSettingsModalOpen && googleAccessToken) {
        try {
          const isValid = await isGoogleSheetsAccessTokenValid(
            googleAccessToken,
          );
          if (!isValid) {
            console.error('Google access token is invalid or expired');
            setConnectedEmail('');
            return;
          }

          const email = await getConnectedEmail(googleAccessToken);
          setConnectedEmail(email);
        } catch (error: any) {
          console.error('Failed to get connected email:', error);
          if (error?.response?.status === 400) {
            console.error('Google access token appears to be invalid');
          }
          setConnectedEmail('');
        }
      } else if (isSettingsModalOpen && !googleAccessToken) {
        setConnectedEmail('');
      }
    };

    getConnectedEmailHandler();
  }, [isSettingsModalOpen, googleAccessToken, setNotification]);

  useEffect(() => {
    const getConnectedEmailHandler = async () => {
      if (isMicrosoftConnected) {
        try {
          const response = await MicrosoftService.getProfile();
          const d = response.data as {
            mail?: string;
            userPrincipalName?: string;
            displayName?: string;
          };
          setConnectedMicrosoftEmail(
            d?.mail ?? d?.userPrincipalName ?? d?.displayName ?? '',
          );
        } catch (error: any) {
          console.error('Failed to get connected Microsoft email:', error);
          if (error?.response?.status === 400) {
            console.error('Microsoft access token appears to be invalid');
          }
          setConnectedMicrosoftEmail('');
        }
      } else if (!isMicrosoftConnected) {
        setConnectedMicrosoftEmail('');
      }
    };

    getConnectedEmailHandler();
  }, [isMicrosoftConnected, setNotification]);

  // Add useEffect to check gmail scope when connected
  useEffect(() => {
    const checkGmailScope = async () => {
      if (isSheetConnected && googleAccessToken) {
        try {
          const hasScope = await hasGmailScope(googleAccessToken);
          setHasGmailScopeEnabled(hasScope);
        } catch (error) {
          console.error('Failed to check gmail scope:', error);
          setHasGmailScopeEnabled(false);
        }
      } else {
        setHasGmailScopeEnabled(false);
      }
    };

    checkGmailScope();
  }, [isSheetConnected, googleAccessToken]);

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  return (
    <Node
      id={id}
      type={type}
      data={data}
      isLoading={false}
      showTokensUsed={false}
    >
      <Box
        sx={{
          maxWidth: '400px',
          p: '16px',
        }}
      >
        <TagInput
          labelName="Output"
          handleId={'input'}
          value={formData.text}
          dataConnected={data?.textData || []}
          readOnly={true}
          hideTextArea={formData.text ? false : true}
          isAdvanced={true}
          disabled={isActiveEdge(edges, id, 'input', 'target')}
          findColor={findColor}
          advancedCollapsed={true}
          isCollapsed={true}
          height={'100px'}
          nodeId={id}
          nodeLabel={data?.label}
          removeIcons={formData.text ? false : true}
        />
        {(data?.textData?.length === 0 || !data?.textData) && (
          <Box
            width={'360px'}
            mt={'-14px'}
            height={'100px'}
            sx={{
              padding: '16px',
              borderRadius: '8px',
              backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2398A2B3FF' stroke-width='2' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: '10px',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <TextAlignCenter size={32} color="#98A2B3" />
              <Typography fontSize={'14px'} color="#98A2B3">
                Your output will be displayed here
              </Typography>
            </Box>
          </Box>
        )}

        <Box mt={'24px'} />

        <Box sx={{ flex: 1, mb: 2 }}>
          <TagInput
            onChange={onChange}
            name="callbackUrl"
            nodeId={id}
            labelName="Callback URL"
            dataConnected={data?.callbackUrlData || []}
            placeholder="Url"
            value={formData?.callbackUrl || ''}
            placement={data?.placement}
            nodeLabel={data?.label}
            handleId={'callbackUrlInput'}
            isActive={isActiveEdge(edges, id, 'callbackUrlInput', 'target')}
            findColor={findColor}
            isCollapsed={false}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Label
              labelName="Send to Email"
              collapsed={formData.enabledEmail}
              onCollapse={() => {
                setFormData({
                  ...formData,
                  enabledEmail: !formData.enabledEmail,
                });
                setIsChangingDirectly(true);
                setWhoIsChanging({
                  value: !formData.enabledEmail,
                  name: 'enabledEmail',
                });
              }}
              sx={{
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '16px',
                color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                background: 'none',
                boxShadow: 'none',
                p: 0,
              }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setIsSettingsModalOpen(true);
            }}
          >
            <Gear size={18} />
          </IconButton>
        </Box>
        <Collapse in={formData.enabledEmail}>
          <TagInput
            name="emails"
            placeholder="Connect or type email(s) separated by “,”"
            onChange={onChange}
            className="nodrag"
            handleId={'emails'}
            labelName="Email Address"
            nodeId={id}
            dataConnected={data?.emailsData || []}
            isAdvanced={true}
            advancedCollapsed={true}
            disabled={isActiveEdge(edges, id, 'emails', 'target')}
            findColor={findColor}
            removeIcons
            nodeLabel={data?.label}
            isCollapsed={true}
            labelStyle={{ fontWeight: 400 }}
            value={formData.emails}
          />
          <TagInput
            name="subject"
            placeholder="Connect or type subject of email"
            onChange={onChange}
            handleId={'subject'}
            value={formData.subject}
            dataConnected={data?.subjectData || []}
            isAdvanced={true}
            labelName="Subject"
            advancedCollapsed={true}
            disabled={isActiveEdge(edges, id, 'subject', 'target')}
            findColor={findColor}
            nodeId={id}
            removeIcons
            isCollapsed={true}
            labelStyle={{
              fontWeight: 400,
            }}
            nodeLabel={data?.label}
          />
          <TagInput
            name="urls"
            placeholder="Attach one or more URLs that you want to send in the email."
            onChange={onChange}
            handleId={'urls'}
            value={formData.urls}
            dataConnected={data?.urlsData || []}
            isAdvanced={true}
            labelName="Attachments"
            advancedCollapsed={true}
            disabled={isActiveEdge(edges, id, 'urls', 'target')}
            findColor={findColor}
            nodeId={id}
            removeIcons
            isCollapsed={true}
            labelStyle={{
              fontWeight: 400,
            }}
            nodeLabel={data?.label}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const hasEmails =
                  (formData.emails && formData.emails.trim()) ||
                  (data?.emailsData && data?.emailsData.length > 0);

                if (hasEmails) {
                  let validCallbackUrl = '';
                  if (formData.callbackUrl && formData.callbackUrl.trim()) {
                    try {
                      new URL(formData.callbackUrl.trim());
                      validCallbackUrl = formData.callbackUrl.trim();
                    } catch (error) {
                      console.error(
                        'Invalid callback URL:',
                        formData.callbackUrl,
                      );
                    }
                  }

                  const emailData: any = {
                    emails: formData.emails || '',
                    subject: formData.subject,
                    text: formData.text,
                    urlsData: data?.urlsData || [],
                  };

                  if (validCallbackUrl) {
                    emailData.callbackUrl = validCallbackUrl;
                  }

                  sendEmails(emailData);
                }
              }}
              disabled={
                (!formData.emails || !formData.emails.trim()) &&
                (!data?.emailsData || data?.emailsData.length === 0)
              }
              sx={{
                backgroundColor: findColor?.color || '#3650DE',
                '&:hover': {
                  backgroundColor: findColor?.color || '#3650DE',
                  opacity: 0.8,
                },
              }}
            >
              Send Email
            </Button>
          </Box>
        </Collapse>
      </Box>
      <Modal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        aria-labelledby="send-email-settings-modal"
        aria-describedby="send-email-settings-modal-desc"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            minWidth: 320,
          }}
        >
          <Typography id="send-email-settings-modal" variant="h6" mb={2}>
            Send to Email Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.useFromPersonalGmail}
                onChange={e =>
                  handleToggleChange('useFromPersonalGmail', e.target.checked)
                }
                size="small"
                disabled={!isSheetConnected}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '14px', color: '#101828' }}>
                  Send from Personal Gmail
                </Typography>
                {!isSheetConnected && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#3650DE',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <ConnectToGoogleSheets />
                    {' to link your Gmail account'}
                  </Typography>
                )}
                {isSheetConnected && !hasGmailScopeEnabled && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#3650DE',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <ConnectToGoogleSheets />
                    {' to enable Gmail sending'}
                  </Typography>
                )}
                {isSheetConnected && hasGmailScopeEnabled && connectedEmail && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#667085',
                      fontWeight: 400,
                    }}
                  >
                    ({connectedEmail})
                  </Typography>
                )}
              </Box>
            }
          />
          <br></br>
          <FormControlLabel
            control={
              <Switch
                checked={formData.useFromMicrosoft}
                onChange={e =>
                  handleToggleChange('useFromMicrosoft', e.target.checked)
                }
                size="small"
                disabled={!isMicrosoftConnected}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '14px', color: '#101828' }}>
                  Send from your Microsoft account
                </Typography>

                {!isMicrosoftConnected && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#3650DE',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <ConnectToMicrosoft />
                    {' to connect your Microsoft account'}
                  </Typography>
                )}

                {isMicrosoftConnected && connectedMicrosoftEmail && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#667085',
                      fontWeight: 400,
                    }}
                  >
                    ({connectedMicrosoftEmail})
                  </Typography>
                )}
              </Box>
            }
          />
          <br></br>
          <FormControlLabel
            control={
              <Switch
                checked={formData.useTemplate}
                onChange={e =>
                  handleToggleChange('useTemplate', e.target.checked)
                }
                size="small"
              />
            }
            label={
              <Typography sx={{ fontSize: '14px', color: '#101828' }}>
                Use Email Template
              </Typography>
            }
          />
        </Box>
      </Modal>
    </Node>
  );
}

export default memo(OutputObjectNode);
